<script>
import {defineComponent, ref, computed, watch, onBeforeMount, reactive} from "vue";
import {useStore} from "vuex";
import {useRouter} from "vue-router";
import {useMeta} from "vue-meta";
import {useI18n} from "vue-i18n";

import Button from "primevue/button";
import Dropdown from "primevue/dropdown";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Paginator from "primevue/paginator";
import InputText from "primevue/inputtext";

import UserFilterModal from "@/components/modals/UserFilterModal.vue";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";

export default defineComponent({
  layout: {name: 'AdminLayout'},
  components: {ConfirmationModal, Dropdown, InputText, Button, DataTable, Column, Paginator, UserFilterModal},
  setup() {
    useMeta({
      title: 'Пользователи'
    });
    const store = useStore();
    const router = useRouter();
    const {t} = useI18n();
    const isLoading = ref(true);
    const search = ref("");
    const visibleModal = computed(() => store.getters.getUserFilterModal);
    const visible = ref(false)
    const deleteUserId = ref(null)
    const visibleFilter = ref(false)
    const users = computed(() => store.getters.getUsersList);
    const roles = computed(() => {
      const all = [...store.getters.getAppRolesList, ...store.getters.getRolesList]
      all.unshift({
        id: 0,
        name: "",
        name_ru: t('pages.users.elements.dropdown-roles')
      })
      return all
    });
    const statusList = computed(() => {
      return [
        {
          id: null,
          name: t('users.filter.all')
        },
        {
          id: 1,
          name: t('users.filter.status.deleted')
        },
        {
          id: 0,
          name: t('users.filter.status.active')
        },
      ]
    })
    const first = ref(0);
    const selectedRole = ref('');
    const selectedStatus = ref(null);

    const toggleVisibleFilter = () => {
      visibleFilter.value = !visibleFilter.value;
      if (visibleFilter.value) {
        selectedRole.value = filterFields.role
        selectedStatus.value = filterFields.deleted
      } else {
        selectedRole.value = null
        selectedStatus.value = null
      }
    }
    const changeVisibleModal = (data) => {
      visible.value = !visible.value;
      if (data) {
        deleteUserId.value = data
      } else {
        deleteUserId.value = null;
      }
    }
    const isDeleteLoading = ref(false);
    const deleteHandler = async () => {
      isDeleteLoading.value = true
      if (deleteUserId.value) {
        await store.dispatch('fetchDeleteUser', {
          id: deleteUserId.value.id
        });
      }
      isDeleteLoading.value = false
      changeVisibleModal(null)
    }
    const findUsers = async () => {
      isLoading.value = true;
      const filterObject = {
        page: ((first.value / (users.value?.data?.pagination?.per_page ?? 1)) + 1),
        ...(search.value ? { search: search.value } : {}),
        ...(filterFields.role?.id ? { role: filterFields.role.id } : {}),
        ...(filterFields.deleted?.id === 0 || filterFields.deleted?.id === 1 ? { deleted: filterFields.deleted?.id } : {}),
      };
      if (selectedRole.value) {
        filterObject['role'] = selectedRole.value.id;
      }
      await store.dispatch(
          'fetchUsers',
          filterObject
      );
      isLoading.value = false;
      window.scrollTo(0, 0);
    };

    const filterFields = reactive({
      role: null,
      deleted: null,
    })

    const filterUsers = async() => {
      isLoading.value = true
      const filterObject = {
        page: 1,
        ...(search.value ? { search: search.value } : {}),
        ...(filterFields.role?.id ? { role: filterFields.role.id } : {}),
        ...(filterFields.deleted?.id === 0 || filterFields.deleted?.id === 1 ? { deleted: filterFields.deleted?.id } : {}),
      };
      await store.dispatch(
          'fetchUsers',
          filterObject
      );
      isLoading.value = false
      window.scrollTo(0, 0);
    }

    const showUserFilterModal = () => {
      store.dispatch('changeStateModal', {
        type: 'userFilterModal',
        bool: !visibleModal.value,
      })
    }

    const toCreateUsers = () => {
      router.push({name: 'user-create'});
    };
    const isSyncLoading = ref(false);

    const syncUsers = async () => {
      isSyncLoading.value = true;
      if (users.value?.data?.data?.length) {
        try {
          for (const user of users.value.data.data) {
            await store.dispatch('fetchSyncUser', user.id)
          }
        } catch (e) {
          console.error(e);
        }
      }
      isSyncLoading.value = false;
    }

    const resetUser = async (id) => {
      await store.dispatch('fetchRestoreUser', {
        id
      });
    }

    const resetFilter = async () => {
      filterFields.role = null
      filterFields.deleted = null
      if (first.value > 0) {
        first.value = 0
      } else {
        await filterUsers()
      }
      toggleVisibleFilter()
    }

    const saveFilter = async () => {
      filterFields.role = selectedRole.value || null
      filterFields.deleted = selectedStatus.value || null
      await filterUsers()
      toggleVisibleFilter()
    }

    watch(() => search.value,
        async () => {
          await filterUsers()
        })

    // watch(selectedRole, async () => {
    //   await filterUsers();
    // })

    watch((first), async (index) => {
      await findUsers();
    });

    onBeforeMount(async () => {
      try {
        await store.dispatch('fetchRoles');
        await store.dispatch('fetchAppRoles');
        await store.dispatch('fetchUsers');
      } catch (e) {
        console.error(e);
      }
      isLoading.value = false
    })

    return {
      search,
      syncUsers,
      selectedRole,
      roles,
      users,
      toCreateUsers,
      first,
      visibleModal,
      showUserFilterModal,
      t,
      isLoading,
      visible,
      deleteUserId,
      changeVisibleModal,
      isDeleteLoading,
      deleteHandler,
      resetUser,
      isSyncLoading,
      visibleFilter,
      toggleVisibleFilter,
      saveFilter,
      selectedStatus,
      statusList,
      resetFilter,
    };
  }
});
</script>

<template>
<!--  <UserFilterModal-->
<!--      filter-data=""-->
<!--      :visible="visibleModal"-->
<!--      :roles="roles"-->
<!--  />-->
  <ConfirmationModal :visible="visibleFilter" closable @closeModal="toggleVisibleFilter" class="border-radius-10">
    <template #header>
      <span class="users__modal-title">{{ t('users.filter.title') }}</span>
    </template>

    <template #default>
      <div class="users__modal-wrapper">
        <div class="users__modal-item">
          <span class="users__modal-label">{{t('users.filter.roles')}}</span>
          <Dropdown
              v-model="selectedRole"
              :options="roles"
              optionLabel="name_ru"
              :placeholder="t('pages.users.elements.dropdown-roles')"
              class="w-full border-radius-15"
          />
        </div>
        <div class="users__modal-item">
          <span class="users__modal-label">{{t('users.filter.status')}}</span>
          <Dropdown
              v-model="selectedStatus"
              :options="statusList"
              optionLabel="name"
              :placeholder="t('pages.users.elements.dropdown-roles')"
              class="w-full border-radius-15"
          />
        </div>
      </div>

    </template>

    <template #footer>
      <div class="flex justify-content-between">
        <Button :label="t('users.filter.clear')" @click="resetFilter" text class="font-light w-12"/>
        <Button :label="t('users.filter.save')" :loading="isDeleteLoading" @click="saveFilter" class="btn-primary font-light ml-3 w-12"/>
      </div>
    </template>
  </ConfirmationModal>

  <ConfirmationModal :visible="visible">
    <template #header>
      {{ t('users.popup.title') }}
    </template>

    <template #default>
      <span>{{ t('users.popup.description') }}</span>
    </template>

    <template #footer>
      <div class="flex justify-content-between mt-4">
        <Button :label="t('labels.cancel')" @click="changeVisibleModal(null)" class="btn-primary-outlined font-light w-12"/>
        <Button :label="t('labels.destroy')" :loading="isDeleteLoading" @click="deleteHandler" class="btn-primary font-light ml-3 w-12"/>
      </div>
    </template>
  </ConfirmationModal>

  <section class="py-2 mb-3">
    <div class="flex justify-content-between align-items-center">
      <h1 class="font-normal">{{ t('pages.users.header') }}</h1>

      <div class="user__actions">
        <button :disabled="isSyncLoading" class="user__button" @click="syncUsers">
          <img src="@/assets/images/icons/Reload.svg" />
          <span>{{t('pages.users.actions.sync')}}</span>
        </button>
        <Button :label="t('pages.users.actions.create')" class="user__button-primary btn-primary" @click="toCreateUsers"/>
      </div>
    </div>
  </section>

  <section class="mb-3 py-2">
    <div class="user__filters">
      <div class="user__search">
        <img src="@/assets/images/icons/Search.svg" class="user__search-icon" />
        <input v-model="search" :placeholder="t('search')" class="user__search-input" />
      </div>
      <button @click="toggleVisibleFilter" class="user__filter">
        <span>{{t('users.filter.title')}}</span>
        <img src="@/assets/images/icons/filter.svg" />
      </button>
<!--      <Dropdown-->
<!--          v-model="selectedRole"-->
<!--          :options="roles"-->
<!--          optionLabel="name_ru"-->
<!--          :placeholder="t('pages.users.elements.dropdown-roles')"-->
<!--          class="w-full md:w-14rem border-radius-15"-->
<!--      />-->
    </div>
    <div>
      <DataTable :value="users.data?.data ? users.data.data : []" :loading="isLoading" showGridlines tableStyle="min-width: 50rem" selectionMode="single">
        <Column field="id" header="ID" class="text-center"/>
        <Column field="first_name" :header="t('tables.users.full-name')">
          <template #body="slotProps">
            <router-link v-if="!slotProps.data.deleted" :to="{ name: 'user-edit', params: { id: slotProps.data.id }}"
                         class="color-black-80 color-primary-hover">
              {{ slotProps.data.last_name ?? '-' }} {{ slotProps.data.first_name ?? '-' }} {{ slotProps.data.patronymic ?? '-' }}
            </router-link>
            <span v-else class="color-black-80 color-primary-hover">{{ slotProps.data.last_name ?? '-' }} {{ slotProps.data.first_name ?? '-' }} {{ slotProps.data.patronymic ?? '-' }}</span>
          </template>
        </Column>
        <Column field="role" :header="t('tables.users.roles')">
          <template #body="slotProps">
            <div v-if="slotProps.data.roles.length" class="flex flex-column">
              <span v-for="role in slotProps.data.roles" :key="role.id">{{ role.name_ru }}</span>
            </div>
            <span v-else>-</span>
          </template>
        </Column>
        <Column field="phone" :header="t('tables.users.contact-number')">
          <template #body="slotProps">
            {{ slotProps.data.phone_code }}{{ slotProps.data.phone }}
          </template>
        </Column>
        <Column field="status" :header="t('tables.users.status')">
          <template #body="slotProps">
            <div class="flex justify-content-center">
              <span :class="['user__status', { active: !slotProps.data.deleted }]">
                {{ slotProps.data.deleted ? t('users.deleted') : t('users.active') }}
              </span>
            </div>
          </template>
        </Column>
        <Column field="action" header="">
          <template #body="slotProps">
            <button v-if="!slotProps.data.deleted" class="user__table-button" @click="changeVisibleModal(slotProps.data)">
              {{ t('users.delete') }}
            </button>
            <button v-else class="user__table-button active" @click="resetUser(slotProps.data.id)">
              {{ t('users.restore') }}
            </button>
          </template>
        </Column>
      </DataTable>
      <Paginator
          v-model:first="first"
          :rows="users?.data?.pagination?.per_page ?? 0"
          :totalRecords="users?.data?.pagination?.total ?? 0"
          class="justify-content-start"
      ></Paginator>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.p-dropdown-panel {
  border-radius: 15px !important;
}
:deep(.p-dropdown-label) {
  display: flex;
  align-items: center;
  border-radius: 4px;
  padding-left: 16px;
}
::v-deep(.p-component-overlay) {
  background-color: rgba(255, 255, 255, 0.6);
}
::v-deep(.p-datatable-loading-icon) {
  path {
    fill: var(--color-primary);
  }
}
.user__actions {
  display: flex;
  justify-content: space-between;
  gap: 30px;
}
.user__filters {
  display: flex;
  gap: 30px;
  margin-bottom: 24px;
}
.user__search {
  display: flex;
  width: 100%;
  position: relative;
}
.user__search-icon {
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
}
.user__search-input {
  padding: 16px 16px 16px 55px;
  width: 100%;
  display: flex;
  height: 55px;
  border-radius: 6px;
  border: 1px solid #CCCCCC;
  font-weight: 400;
  font-size: 16px;
  line-height: 25px;
  vertical-align: middle;

  &:focus {
    border: 1px solid var(--color-primary);
    outline: none;
  }
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(-360deg); }
}
.user__button {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  background: #FFFFFF;
  border-radius: 4px;
  border: none;
  color: #333333;
  cursor: pointer;
  img {
    animation: unset;
  }
  &:hover {
    background: #FFFFFF !important;
    color: #333333 !important;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    img {
      animation: spin 1s linear infinite;
    }
  }
}
.user__button-primary {
  padding: 12.5px 35.5px;
}
.user__status {
  display: flex;
  align-items: center;
  padding: 5px 12px;
  background: #E26B6B;
  font-weight: 600;
  font-size: 13px;
  line-height: 22px;
  text-align: center;
  color: white;
  border-radius: 30px;

}
.user__status.active {
  background: #9CC5E1;
}
.user__table-button {
  border: none;
  background: none;
  font-weight: 600;
  font-size: 13px;
  line-height: 20px;
  text-align: center;
  text-decoration: underline;
  text-decoration-style: solid;
  color: #E26B6B;
  cursor: pointer;
  min-width: 100px;
}
.user__table-button .active {
  color: #666666;
}
.user__filter {
  width: 228px;
  background: #F8F8F8;
  border: 1px solid #CCCCCC;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  cursor: pointer;
  border-radius: 4px;
  font-weight: 400;
  font-size: 16px;
  line-height: 25px;
  vertical-align: middle;

  &:hover {
    border: 1px solid var(--color-primary) !important;
    //opacity: 0.6;
  }
}
::v-deep(.p-button.p-button-text) {
  color: #333333;
  &:hover {
    color: #333333 !important;
    background: white !important;
  }
}
.users__modal-title {
  font-weight: 600;
  font-size: 18px;
  line-height: 100%;
  color: #333333;
}
.users__modal-wrapper {
  display: flex;
  flex-direction: column;
  width: 415px;

}
.users__modal-item {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 20px;
  &:first-child {
    border-bottom: 1px solid #E1E8ED;
    padding-bottom: 20px;
    padding-top: 0;
  }
  ::v-deep(.p-dropdown) {
    border-radius: 6px;
    .p-dropdown-label {
      padding: 11px 16px !important;
      font-weight: 400;
      font-family: "RF Dewi", sans-serif !important;
      font-size: 0.875rem !important;
      line-height: 22px;
      vertical-align: middle;
    }
  }

}
.users__modal-label {
  font-weight: 600;
  font-size: 14px;
  line-height: 21px;
  color: #333333;
}

</style>
